import React, { createContext, useContext, useState, useEffect } from "react";
import { decrypt } from "../actions/common";

const MeetingListContext = createContext();

export const useMeetingList = () => {
  const context = useContext(MeetingListContext);
  if (!context) {
    throw new Error("useMeetingList must be used within a MeetingListProvider");
  }
  return context;
};

const useMeetingListInternal = () => {
  const [currentUser, setCurrentUser] = useState({});
  const [loading, setLoading] = useState(false);

  const [usersList, setUsersList] = useState([]);

  const [status, setStatus] = useState("loading");
  const [authUrl, setAuthUrl] = useState("");
  const [events, setEvents] = useState([]);

  var has_local_data = localStorage.getItem("encdata") ? 1 : 0;
  var localdata = has_local_data
    ? decrypt(localStorage.getItem("encdata"), 0)
    : "";
  const userId = parseInt(has_local_data ? localdata.user_id : 0);

  const fetchUsersList = () => {
    fetch(`${process.env.REACT_APP_CRM}/user-list`, {
      method: "POST",
    })
      .then((resp) => {
        return resp.json();
      })
      .then((data) => {
        if (data.code == 200) {
          const userList = data.data;
          setUsersList(userList);

          const userData = data.data;
          for (const key in userData) {
            if (userData.hasOwnProperty(key)) {
              const user = userData[key];
              if (user.emp_id == userId) {
                setCurrentUser({ ...user, userId: user.emp_id });
                break;
              }
            }
          }
        }
      });
  };

  useEffect(() => {
    fetchUsersList();
  }, []);

  useEffect(() => {
    if (!currentUser.email) return;
    // Reset every time the modal opens
    setStatus("loading");
    setAuthUrl("");

    fetchMeetings();
  }, [currentUser]);

  const fetchMeetings = async (em = "") => {
    if (!currentUser.email && !em) {
      return;
    }

    const userEmail = (em || currentUser.email).toLowerCase();

    setLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_CRM}/gmail/meetings/list`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email: userEmail }),
        },
      );

      const data = await response.json();
      if (data.code === "200" && data.status === "okay") {
        setStatus("authorized");

        const list = Array.isArray(data) ? data : data.events || [];

        const mapped = list.map((meeting) => ({
          id: meeting.id,
          google_event_id: meeting.id, // Keeping id as google_event_id for update
          title: meeting.title || "Untitled",
          start: new Date(meeting.start),
          end: new Date(meeting.end),
          allDay: meeting.is_all_day || false,
          // flat props for easy access in callbacks
          meet_link: meeting.meet_link,
          status: meeting.status,
          organizer: meeting.organizer,
          is_organizer: meeting.is_organizer,
          description: meeting.description,
          html_link: meeting.html_link,
          attendees: meeting.attendees || [],
        }));

        setEvents(mapped);
      } else if (data.code === "404" && data.status === "reauth_required") {
        setAuthUrl(data.url || "");
        setStatus("reauth");
        setEvents([]);
      } else {
        // Unexpected response – treat as reauth to surface the message
        setStatus("reauth");
        setEvents([]);
      }
    } catch (err) {
      console.error("Fetch meetings error:", err);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  return {
    events,
    status,
    authUrl,
    loading,
    fetchMeetings,
    usersList,
    currentUser,
  };
};

export const MeetingListProvider = ({ children }) => {
  const meetingListData = useMeetingListInternal();
  return (
    <MeetingListContext.Provider value={meetingListData}>
      {children}
    </MeetingListContext.Provider>
  );
};

export default useMeetingListInternal;

export const withMeetingList = (WrappedComponent) => {
  return function WithMeetingList(props) {
    const {
      events = [],
      status,
      authUrl,
      loading,
      fetchMeetings,
      usersList,
      currentUser,
    } = useMeetingList();

    return (
      <WrappedComponent
        {...props}
        events={events}
        meetingListLoading={loading}
        fetchMeetings={fetchMeetings}
        usersList={usersList}
        currentUser={currentUser}
        authUrl={authUrl}
        status={status}
      />
    );
  };
};



// WEBPACK FOOTER //
// ./src/reducers/meetingList.js